/**
 * 孩子详情（家长端）。
 *
 * 覆盖四件事：
 *  1. 基本信息与年级（年级决定孩子端界面模式）
 *  2. **uiModeOverride**：家长手动覆盖"按年级自动判定"的儿童模式（见方案 2.5）
 *  3. 共享：邀请另一位家长（owner 才能改）、查看已共享的家长、取消
 *  4. 重置孩子密码：新密码只在响应里出现一次，需要立刻展示
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GENDERS, GRADES, gradeLabel, UI_MODES, type UiMode } from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { CommentBox } from '../../components/CommentBox';
import { useToast } from '../../components/ui/Toast';
import {
  useResetStudentPassword,
  useShareMutations,
  useStudent,
  useStudentPoints,
  useStudentShares,
  useUpdateStudent,
} from '../../lib/queries';
import { toErrorMessage } from '../../lib/api/error';
import { SHARE_PERMISSION_LABEL } from '../../lib/api/shares';
import {
  POINTS_PLACEHOLDER,
  balanceDisplayText,
  resolveBalanceDisplay,
} from '../../lib/pointsDisplay';
import { formatDateTimeFull } from '../../lib/utils';

const UI_MODE_LABEL: Record<UiMode, string> = {
  child: '儿童模式（超大按钮、大字号）',
  bridge: '过渡模式（中等按钮）',
  full: '标准模式（常规信息密度）',
};

export function StudentDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();

  const studentQuery = useStudent(id);
  const sharesQuery = useStudentShares(id);
  const pointsQuery = useStudentPoints(id);
  const updateStudent = useUpdateStudent(id);
  const resetPassword = useResetStudentPassword(id);
  const { create, cancel } = useShareMutations();

  const student = studentQuery.data ?? null;

  // ---- 「当前积分」的三种情形（真 0 / 拿不到 / 加载中）----
  // 判定逻辑在 `lib/pointsDisplay.ts`：这里**不再**写 `?? 0` —— 那个兜底会把
  // "积分接口失败"显示成一个看起来完全正常的 **0 积分**。
  const balanceInput = {
    points: pointsQuery.data,
    pointsLoading: pointsQuery.isLoading,
    profileBalance: student?.pointsBalance,
    profileLoading: studentQuery.isLoading,
  };
  const balanceState = resolveBalanceDisplay(balanceInput);
  const balanceText = balanceDisplayText(balanceInput);

  // ---- 共享名单：已生效（accepted）与待同意（pending）分开 ----
  const sharesQueryData = sharesQuery.data;
  const activeShares = sharesQueryData?.shares ?? [];
  const pendingInvites = sharesQueryData?.pendingInvites ?? [];

  // ---- 基本信息表单 ----
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState<string>(GENDERS[0]);
  const [birthday, setBirthday] = useState('');
  const [gradeCode, setGradeCode] = useState<string>(GRADES[0].code);
  const [uiModeOverride, setUiModeOverride] = useState<string>('');

  useEffect(() => {
    if (!student) return;
    setNickname(student.nickname);
    setGender(student.gender);
    setBirthday(student.birthday);
    setGradeCode(student.gradeCode);
    setUiModeOverride(student.uiModeOverride ?? '');
  }, [student]);

  // ---- 共享邀请表单 ----
  const [target, setTarget] = useState('');
  const [permission, setPermission] = useState<'editor' | 'viewer'>('editor');

  // ---- 重置密码结果 ----
  const [newPassword, setNewPassword] = useState<string | null>(null);
  /**
   * 待处理的共享：`accepted` = 取消共享，`pending` = 撤回邀请。
   *
   * 两者都**必须确认**：取消共享会立即吊销另一位家长对孩子的访问权，
   * 而它就排在共享列表的按钮行里，手机上一误触就会发生 ——
   * 虽然可以重新邀请，但对方会突然失去访问权且不知道为什么。
   * 撤回一条还没被同意的邀请影响小一些，但按钮同样在列表里，一并确认。
   */
  const [pendingCancelShare, setPendingCancelShare] = useState<{
    id: string;
    nickname: string;
    username: string;
    permission: string;
    /** `pending` = 邀请还没被同意，文案要说"撤回邀请"而不是"取消共享"。 */
    status: 'pending' | 'accepted';
  } | null>(null);

  const isOwner = student?.permission === 'owner';
  const canEdit = student?.permission === 'owner' || student?.permission === 'editor';

  const saveProfile = async () => {
    try {
      await updateStudent.mutateAsync({
        nickname,
        gender: gender as 'male' | 'female',
        birthday,
        gradeCode,
        uiModeOverride: uiModeOverride ? (uiModeOverride as UiMode) : null,
      });
      toast.showToast('已保存', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const invite = async () => {
    const value = target.trim();
    if (!value) {
      toast.showToast('请填写对方的用户名或邮箱', 'error');
      return;
    }
    const looksLikeEmail = value.includes('@');
    try {
      await create.mutateAsync({
        studentId: id,
        permission,
        ...(looksLikeEmail ? { targetEmail: value } : { targetUsername: value }),
      });
      setTarget('');
      toast.showToast('邀请已发出，等待对方同意', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const doResetPassword = async () => {
    try {
      const result = await resetPassword.mutateAsync({ forceChangeOnNextLogin: true });
      setNewPassword(result.initialPassword ?? '（已按你的设置更新）');
      toast.showToast('已重置密码', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  return (
    <AppShell
      kind="parent"
      title={student ? student.nickname : '孩子详情'}
      /*
        ⚠️ 这里**刻意没有**任何 `headerAction` 里的「返回」按钮（2026-10 用户真机实测）。

        改之前它是 `onClick={() => navigate(-1)}`（**浏览器历史后退**），
        而历史里刚离开的一页很可能就是本页的下钻子页（学校里最常走的路径是
        "孩子详情 → 学校作业"），于是：
            孩子详情 → 点「返回」→ 学校作业 → 点「返回」→ 孩子详情 → ……
        用户在两张截图之间来回弹，原话是"点返回键的话，会在两个页面之间不断切换"。

        现在返回入口**只有左上角那一个「←」**，目标是层级上的上一级 ——
        **孩子列表就是首页 `/`**（`ParentHomePage`，标题栏写着「我的孩子」），
        由 `lib/backNav.ts` 按当前路由推导，与历史无关、也不可能成环。

        ⚠️ 这里曾经指向 `/students`（"列表的语义路径"）：**那条路由没有注册**，
        于是真机上一点「←」就落到 `*` 兜底 → 「页面不存在」。返回目标只认
        `App.tsx` 里注册过的路由（`backNav.test.ts` 会逐条核对，名单见 `src/routes.ts`）。
      */
    >
      {studentQuery.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : !student ? (
        <EmptyState
          icon="🔍"
          title="没找到这个孩子"
          description="可能是链接失效，或你没有查看权限。"
          action={
            <Link to="/" className="mt-2 text-sm font-medium text-brand-600 hover:underline">
              返回孩子列表
            </Link>
          }
        />
      ) : (
        <div className="space-y-6">
          {/* ---------- 概览 ---------- */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-slate-900">{student.nickname}</h2>
                  <Badge tone={isOwner ? 'brand' : 'neutral'}>
                    {isOwner ? '我创建的' : '共享给我'}
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {gradeLabel(student.gradeCode)} · 用户名 {student.username}
                </p>
              </div>
              <div className="rounded-2xl bg-brand-50 px-4 py-3 text-center">
                {/*
                  ⚠️ 这里原来是 `pointsQuery.data?.balance ?? student.pointsBalance ?? 0`：
                     `GET /students/:id` 不返回 `pointsBalance`，而 `studentPoints()` 在
                     404/501 时兜底成 `{ balance: 0 }` —— 于是**积分接口一失败就显示
                     "0 积分"**，一个看起来完全正常的假数字（与"孩子卡片 0 积分"
                     "统计页恒为 0"同一类病）。
                  现在三态分开：真 0 显示「0」／读不到显示「—」／加载中显示骨架。
                */}
                {balanceState === 'loading' ? (
                  <div aria-label="积分加载中" role="status">
                    <Skeleton className="mx-auto h-8 w-12" />
                  </div>
                ) : (
                  <p
                    className="text-2xl font-bold text-brand-700"
                    title={balanceState === 'unknown' ? '暂时取不到积分余额' : undefined}
                  >
                    {balanceText ?? POINTS_PLACEHOLDER}
                  </p>
                )}
                <p className="text-xs text-brand-700">当前积分</p>
              </div>
            </div>
            {/*
              ---------- 入口按钮排：等宽三列 ----------
              2026-10 用户实测原话："孩子详情页里面，他们那几个功能按钮能不能对齐一下？
              因为有的按钮大小不一致嘛。但你每一列的按钮要左对齐或者是右对齐。"

              改之前是 `flex flex-wrap gap-2` + 每个按钮**按内容自适应宽度**：
              同一行里「目标」(2 字) 与「学校作业」(4 字) 宽度不同，换行位置又取决于
              视口宽度 → 每一行、每一列的左右边缘都对不齐，看起来参差。

              现在改成**等宽三列网格**：`grid-cols-3` 在 Tailwind 里是
              `repeat(3, minmax(0, 1fr))` —— 三列严格等宽，而且 `minmax(0, …)`
              允许轨道收缩到内容宽度以下（不会把按钮顶出卡片）。
              每个 `<Link>` 是网格项、被块级化后自动撑满整格，按钮再用 `fullWidth`
              撑满 `<Link>`，于是**行、列天然对齐**，也不再有参差。

              ⚠️ 顺序、文案、跳转目标、权限判定、显隐条件、图标**一律没动**，
                 这一处**纯排版**。
              ⚠️ 第 7 个「统计」落在最后一行的**第 1 格**、只占一格
                 （网格默认 `grid-column: auto`，不会撑满整行）。
              ⚠️ `min-w-0` 沿用本项目的窄屏约定（内容列可收缩、按钮列不参与收缩）；
                 这里**刻意不用** `flex-1` —— 网格轨道自己就等宽，
                 `flex-1` 的 `flex-basis: 0` 正是本项目修过好几次
                 "文字被挤成一列"的那个坑。

              ---- 320px 那一档为什么是**两列**（2026-10 无头 Edge 实测，不是猜的）----
              三列要放下四个汉字，需要满足：
                  每格 ≥ 56(四个汉字@14px) + 24(px-3 ×2) + 2(border ×2) = 82px
                  每格 = (视口 - 32[main px-4] - 2[卡片 border] - 32[卡片 p-4] - 16[两个 gap]) / 3
              解出来 **视口 ≥ 328px** 才够。实测 320px 时每格只有 79.33px、
              内容盒 53px < 56px ⇒「学校作业 / 语文听写 / 积分明细 / 奖品目录 / 兑换审批」
              会被折成两行，文字还上下各溢出 1px（`getBoundingClientRect` 实测
              textTop=-1 / textBottom=37，按钮只有 36px 高）。

              ⚠️ 不靠"把字号调小"或"压 padding"去硬塞：`Button` 的 `px-3` 来自
                 `SIZES.sm`，而本项目的 `cn()` 是**纯字符串拼接**（没有 tailwind-merge），
                 调用点再传一个 `px-2.5` 同权重、只会被样式表顺序吃掉 —— 那种"看起来能覆盖
                 其实不一定生效"的写法不能留。所以让**列数**在窄屏退一步：
                 320px 用**两列等宽**（每格 123px，四个汉字 67px 富余），
                 ≥360px 回到**三列等宽**（用户选定的方案；360px 每格 92.67px、
                 内容盒 67px > 56px，实测单行不折）。
                 阈值取 360 而不是 `sm`(640)，是因为 390/414 这些常见手机宽度
                 三列本来就好好的，用 `sm` 等于把它们的对齐也一起退掉。
            */}
            <div className="mt-4 grid grid-cols-2 gap-2 min-[360px]:grid-cols-3">
              <Link to={`/students/${id}/nodes`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  目标
                </Button>
              </Link>
              {/*
                「打卡任务」这个入口**故意不放在这里**（2026-10-14 用户实测）：
                打卡任务是**挂在叶子目标上**配置的，要配置它必须先选中目标 ——
                「目标」页里那一行的「⋯ → 加打卡任务」才是它自然的入口。
                在这一排入口里再放一个"打卡任务"，点过去只是一张没有上下文的清单，
                用户原话是"这个打卡任务没有什么作用，可以去掉"。

                ⚠️ **2026-10-31 更新**：那个「打卡任务总览」页**已经整体删除**了
                （用户明确要求）。它的两块内容搬进了「目标」页底部：
                「调整打卡任务的顺序」与「看一下孩子端」。
                所以现在打卡任务只有**一个**入口：目标页里那一行的「⋯ → 加打卡任务」。
              */}
              {/* 学校作业：每天都要重新录一次，所以入口与「目标」并列，
                  而不是藏进「目标」页里 —— 它是家长每天都要点的一个入口。 */}
              <Link to={`/students/${id}/homework`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  学校作业
                </Button>
              </Link>
              {/*
                语文听写（**第三步**：听写设置 / 布置听写 / 做听写）—— 2026-10-15 加上。

                用户原话："针对每一个小孩的听写设置、听写布置以及听写任务的布置，
                就可以放在某个具体的孩子下面。" 所以这一条**在这里**，而
                **数据录入**（课本/课文/生字/词语）在顶层工具页
                `/tools/dictation` —— 字表是全家共用的（那 6 张字典表里没有
                `student_id`），挂到某个孩子下面会让人以为是"这个孩子的专属字表"。

                ⚠️ 两件事的区别写在按钮文案上：这里叫「语文听写」（这个孩子的），
                   工具页那一页叫「语文听写（字表录入）」。
              */}
              <Link to={`/students/${id}/dictation`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  语文听写
                </Button>
              </Link>
              <Link to={`/students/${id}/points`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  积分明细
                </Button>
              </Link>
              <Link to={`/students/${id}/rewards`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  奖品目录
                </Button>
              </Link>
              <Link to={`/students/${id}/redemptions`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  兑换审批
                </Button>
              </Link>
              <Link to={`/students/${id}/stats`} className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  统计
                </Button>
              </Link>
            </div>
          </Card>

          {/* ---------- 基本信息 ---------- */}
          <Card>
            <CardHeader
              title="基本信息"
              description="年级决定孩子端的界面模式：小学低年级自动使用大按钮儿童模式。"
            />
            {!canEdit ? (
              <p className="text-sm text-slate-500">你是「仅查看」权限，无法修改孩子档案。</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="昵称"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                  />
                  <Input
                    label="出生日期"
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Select
                    label="性别"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    {GENDERS.map((value) => (
                      <option key={value} value={value}>
                        {value === 'male' ? '男孩' : '女孩'}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label="年级"
                    value={gradeCode}
                    onChange={(e) => setGradeCode(e.target.value)}
                  >
                    {GRADES.map((grade) => (
                      <option key={grade.code} value={grade.code}>
                        {gradeLabel(grade.code)}
                      </option>
                    ))}
                  </Select>
                </div>

                <Select
                  label="孩子端界面模式"
                  value={uiModeOverride}
                  onChange={(e) => setUiModeOverride(e.target.value)}
                  hint="留空表示按年级自动判定；也可以手动强制成某一种"
                >
                  <option value="">跟随年级（推荐）</option>
                  {UI_MODES.map((mode) => (
                    <option key={mode} value={mode}>
                      {UI_MODE_LABEL[mode]}
                    </option>
                  ))}
                </Select>

                <div className="flex justify-end">
                  <Button loading={updateStudent.isPending} onClick={() => void saveProfile()}>
                    保存修改
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* ---------- 点评（积分的配套，见方案 3.4.4） ---------- */}
          <CommentBox
            targetType="period"
            targetId={id}
            studentId={id}
            title="给孩子写段点评"
            description="挂在孩子这一段成长上。快捷短语可以少打字；儿童模式下孩子看到的是图形化表情。"
            readOnly={!canEdit}
          />

          {/* ---------- 密码 ---------- */}
          <Card>
            <CardHeader
              title="登录密码"
              description="孩子忘记密码时，你可以重置；重置后建议让孩子自己改成好记的密码。"
            />
            {newPassword && (
              <div className="mb-4 rounded-xl bg-emerald-50 p-3 text-center">
                <p className="text-xs text-emerald-800">新的初始密码（只显示这一次）</p>
                <p className="mt-1 select-all break-all font-mono text-xl font-bold text-emerald-900">
                  {newPassword}
                </p>
              </div>
            )}
            {canEdit ? (
              <Button
                variant="secondary"
                loading={resetPassword.isPending}
                onClick={() => void doResetPassword()}
              >
                重置密码并生成新的初始密码
              </Button>
            ) : (
              <p className="text-sm text-slate-500">你没有重置密码的权限。</p>
            )}
          </Card>

          {/* ---------- 共享 ---------- */}
          <Card>
            <CardHeader
              title="与另一位家长共享"
              description="邀请后对方可以查看进度；选择「可编辑」还能新建目标和打卡任务。打卡与兑换始终只有孩子自己能做。"
            />

            {isOwner ? (
              <div className="space-y-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <Input
                      label="对方的用户名或邮箱"
                      value={target}
                      onChange={(e) => setTarget(e.target.value)}
                      placeholder="例如 mama 或 mama@example.com"
                      autoComplete="off"
                    />
                  </div>
                  <Select
                    label="权限"
                    className="sm:w-40"
                    value={permission}
                    onChange={(e) => setPermission(e.target.value as 'editor' | 'viewer')}
                  >
                    <option value="editor">可编辑</option>
                    <option value="viewer">仅查看</option>
                  </Select>
                  <Button loading={create.isPending} onClick={() => void invite()}>
                    发送邀请
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                只有孩子的创建者可以发起或调整共享。你是共享家长，不能转共享给别人。
              </p>
            )}

            <div className="mt-5 space-y-2">
              <h3 className="text-sm font-semibold text-slate-700">已共享的家长</h3>
              {/*
                ⚠️ 这一份名单**只列 `status = 'accepted'`**（对方现在确实能看这个孩子）。
                   `revoked`（owner 取消）/ `left`（对方退出）/ `rejected` / `expired`
                   一律不在名单里 —— 以前服务端的 WHERE 没有状态过滤，被取消共享的人
                   仍然留在这张名单上，看起来还有权限（2026-10-14 定位的隐患）。
                   `pending`（邀请发了但对方还没同意）也不在这里：它**还没生效**，
                   冒充成"已共享"就是在骗人（见下面单独的那一段）。
              */}
              {sharesQuery.isLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : activeShares.length === 0 ? (
                <p className="text-sm text-slate-500">
                  还没有其他家长可以看到这个孩子。
                  {pendingInvites.length > 0 && '（下面有一条邀请还在等对方同意）'}
                </p>
              ) : (
                <ul className="space-y-2">
                  {activeShares.map((share) => {
                    const name = share.parentNickname || share.parentUsername;
                    // 昵称与用户名相同时不重复显示（生产库里 Lena 两者都是 "Lena"）
                    const showUsername = Boolean(name) && share.parentUsername !== name;
                    return (
                      <li
                        key={share.id}
                        className="flex items-center justify-between rounded-xl border border-slate-200 p-3"
                      >
                        <div className="min-w-0">
                          {/*
                            ⚠️ 这一行**必须**显示出对方是谁。原来的写法是直接打印
                            `share.parentNickname` / `share.parentUsername`，而模型里这两个
                            字段从来没被后端填过（后端叫 `toParent*`）→ 渲染成空字符串，
                            只剩「可编辑 · 10-13 20:55」，没有任何报错。
                            现在：昵称优先、没有昵称就用用户名；两者都取不到时**显式**写出
                            「未知家长」，让"没读到"看得见（与积分显示「—」同一个原则）。
                          */}
                          <p className="truncate text-sm font-medium text-slate-900">
                            {name || '未知家长'}
                            {showUsername && (
                              <span className="ml-2 font-normal text-slate-500">
                                {share.parentUsername}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">
                            {SHARE_PERMISSION_LABEL[share.permission]} ·{' '}
                            {formatDateTimeFull(share.createdAt)}
                          </p>
                        </div>
                        {isOwner && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() =>
                              setPendingCancelShare({
                                id: share.id,
                                nickname: name || '未知家长',
                                username: share.parentUsername,
                                permission: SHARE_PERMISSION_LABEL[share.permission],
                                status: 'accepted',
                              })
                            }
                          >
                            取消共享
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/*
              ---------- 待对方同意的邀请 ----------
              这些是 `status = 'pending'`：邀请已经发出，**对方还没有同意，此刻看不到孩子**。
              单独列出来（而不是混进「已共享的家长」）有两个理由：
                1. 混进去就是在冒充已生效；
                2. 但也不能藏起来 —— 否则家长看不到"我发出的邀请还在等对方处理"，
                   只能反复重发（服务端的唯一索引还会把它收敛成同一条）。
              没有邀请时**整段不渲染**（加载中也不占位）：这里刻意不给它骨架屏 ——
              它是一条**附加**信息，多数家庭根本没有待处理的邀请，
              为一个通常为空的区块先亮一块灰条，只会让页面显得在抖。
            */}
            {pendingInvites.length > 0 ? (
              <div className="mt-5 space-y-2">
                <h3 className="text-sm font-semibold text-slate-700">待对方同意的邀请</h3>
                <p className="text-xs text-slate-400">
                  对方同意之后才会出现在上面的「已共享的家长」里。
                </p>
                <ul className="space-y-2">
                  {pendingInvites.map((invite) => {
                    const name = invite.parentNickname || invite.parentUsername;
                    const showUsername = Boolean(name) && invite.parentUsername !== name;
                    return (
                      <li
                        key={invite.id}
                        className="flex items-center justify-between rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-700">
                            {name || '未知家长'}
                            {showUsername && (
                              <span className="ml-2 font-normal text-slate-500">
                                {invite.parentUsername}
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400">
                            等待同意 · {SHARE_PERMISSION_LABEL[invite.permission]} ·{' '}
                            {formatDateTimeFull(invite.createdAt)}
                          </p>
                        </div>
                        {isOwner && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() =>
                              setPendingCancelShare({
                                id: invite.id,
                                nickname: name || '未知家长',
                                username: invite.parentUsername,
                                permission: SHARE_PERMISSION_LABEL[invite.permission],
                                status: 'pending',
                              })
                            }
                          >
                            撤回邀请
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </Card>
        </div>
      )}

      {/* ---------- 取消共享 / 撤回邀请 确认 ----------
          取消共享会立即吊销对方对这个孩子的访问权，须先确认；
          撤回一条还没被同意的邀请走同一个端点（`revoke`），但文案必须不一样。 */}
      <Modal
        open={pendingCancelShare !== null}
        onClose={() => setPendingCancelShare(null)}
        title={pendingCancelShare?.status === 'pending' ? '撤回邀请' : '取消共享'}
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={() => setPendingCancelShare(null)}>
              取消
            </Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              onClick={() => {
                const target = pendingCancelShare;
                if (!target) return;
                void cancel
                  .mutateAsync({ studentId: id, shareId: target.id })
                  .then(() =>
                    toast.showToast(
                      target.status === 'pending' ? '已撤回邀请' : '已取消共享',
                      'success',
                    ),
                  )
                  .catch((err: unknown) => toast.showToast(toErrorMessage(err), 'error'))
                  .finally(() => setPendingCancelShare(null));
              }}
            >
              {pendingCancelShare?.status === 'pending' ? '确认撤回邀请' : '确认取消共享'}
            </Button>
          </div>
        }
      >
        {pendingCancelShare?.status === 'pending' ? (
          <>
            <p className="text-sm text-slate-600">
              要撤回发给 <strong>{pendingCancelShare?.nickname}</strong>
              （{pendingCancelShare?.username}）的邀请吗？
            </p>
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
              对方<strong>还没有同意</strong>，所以此刻并没有任何访问权；撤回后这条邀请会失效，
              收件箱里也不会再出现。需要时你可以重新邀请。
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              要让 <strong>{pendingCancelShare?.nickname}</strong>
              （{pendingCancelShare?.username}）不再看到这个孩子吗？
            </p>
            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
              对方当前的权限是「{pendingCancelShare?.permission}」，取消后会<strong>立即失去访问权</strong>。
              如果只是想调整权限，请改用「修改权限」；需要时你可以重新邀请对方。
            </p>
          </>
        )}
      </Modal>
    </AppShell>
  );
}
